
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import {
  setAnalyticsUser,
  clearAnalyticsUser,
} from '@/lib/analytics/ga';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, firstName: string, lastName: string, acceptTerms: boolean, role?: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  supabase: typeof supabase;
  userCountry: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Loads role + grade for analytics user_properties (no PII), and flips
 * consent to granted if the user has accepted T&C. Defers DB reads with
 * setTimeout to avoid blocking the auth state change handler.
 */
async function syncAnalyticsForUser(userId: string) {
  try {
    const [{ data: roleRow }, { data: prefs }] = await Promise.all([
      supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle(),
      supabase
        .from('user_learning_preferences' as any)
        .select('grade')
        .eq('user_id', userId)
        .maybeSingle(),
    ]);

    setAnalyticsUser({
      userId,
      role: (roleRow as { role?: string } | null)?.role ?? null,
      grade: (prefs as { grade?: string } | null)?.grade ?? null,
    });
    // Note: cookie consent is controlled exclusively by CookieConsentBanner.
  } catch (err) {
    // Analytics must never break auth.
    console.warn('Analytics sync failed:', err);
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userCountry, setUserCountry] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
        } else if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user) {
            setTimeout(() => syncAnalyticsForUser(session.user.id), 0);
          }
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        setUser(session?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_OUT' || !session?.user) {
          clearAnalyticsUser();
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          // Defer to avoid recursive Supabase calls inside the listener.
          setTimeout(() => syncAnalyticsForUser(session.user.id), 0);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Error in signOut:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string, acceptTerms: boolean, role: string = 'user') => {
    try {
      // Create the account + send the confirmation email through our own SMTP
      // sender (Supabase's built-in sender is not configured for this project).
      const { data, error } = await supabase.functions.invoke('signup-with-email', {
        body: {
          email,
          password,
          firstName,
          lastName,
          acceptTerms,
          role,
          redirectTo: window.location.origin,
        },
      });

      if (!error && data?.success) {
        return { error: null };
      }

      // Surface validation errors returned by the function (4xx responses).
      let fnMessage: string | null = data?.error ?? null;
      const ctx = (error as any)?.context;
      if (!fnMessage && ctx && typeof ctx.json === 'function') {
        try {
          const parsed = await ctx.clone().json();
          fnMessage = parsed?.error ?? null;
        } catch (_) {
          // ignore
        }
      }
      if (fnMessage) {
        return { error: new Error(fnMessage) };
      }


      // Fallback to Supabase's built-in signup if the function is unavailable.
      const redirectUrl = `${window.location.origin}/auth?type=signup`;
      const { error: fallbackError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            accept_terms: acceptTerms,
            role: role,
          },
        },
      });
      return { error: fallbackError };
    } catch (error) {
      return { error };
    }
  };


  const resetPassword = async (email: string) => {
    try {
      // Send via our own SMTP-backed edge function for reliable delivery.
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { email, redirectTo: window.location.origin },
      });
      if (error) {
        // Fallback to Supabase's built-in sender if the function is unavailable.
        const { error: fallbackError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth?type=recovery`,
        });
        return { error: fallbackError };
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  };


  const value = {
    user,
    loading,
    signOut,
    signIn,
    signUp,
    resetPassword,
    supabase,
    userCountry,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
